import Backbone from 'backbone';
import QRCode from 'qrcode';

import { IntegrationSystem, PunchhPrinterBarcodeType } from '@biteinc/enums';
import { StringHelper, Time } from '@biteinc/helpers';

const sampleOrder = {
  guestFirstName: 'Sample',
  guestLastName: 'Guest',
  guestEmail: 'sample@example.com',
  guestPhoneNumber: '(555) 010-0138',
  orderNumber: 'SAMPLE',
  total: '$4.40',
};

function locationAddress(address) {
  if (!address) {
    return '';
  }
  return [
    address.crossStreet,
    address.line1,
    address.line2,
    [address.city && `${address.city},`, address.state, address.postalCode]
      .filter(Boolean)
      .join(' '),
  ]
    .filter(Boolean)
    .join('\n');
}

function escapeTemplateValue(value) {
  return String(value).replace(/[&<>"']/g, (character) => {
    return {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[character];
  });
}

app.ReceiptSampleView = Backbone.View.extend({
  className: 'receipt-live-preview',

  initialize(options) {
    this.fieldGroupView = options.fieldGroupView;
  },

  value(field) {
    return this.fieldGroupView.getValueForFieldName(field);
  },

  templateTokens() {
    const location = app.location;
    const orgName = location?.get('orgName') || app.org?.get('name') || '';
    const orderedAt = Time.moment(new Date(), location?.get('timezone') || 'UTC').format(
      'MMM DD YYYY, h:mm A',
    );
    return {
      BRAND_NAME: this.fieldGroupView.model?.get('brandName') || orgName,
      GUEST_EMAIL: sampleOrder.guestEmail,
      GUEST_NAME: `${sampleOrder.guestFirstName} ${sampleOrder.guestLastName}`,
      GUEST_FIRST_NAME: sampleOrder.guestFirstName,
      GUEST_LAST_NAME: sampleOrder.guestLastName,
      GUEST_PHONE_NUMBER: sampleOrder.guestPhoneNumber,
      LOCATION_NAME: location?.get('name') || '',
      LOCATION_PHONE_NUMBER: location?.get('phoneNumber')
        ? StringHelper.toFormattedPhoneNumber(location.get('phoneNumber'))
        : '',
      LOCATION_ADDRESS: locationAddress(location?.get('fullAddress')),
      ORG_NAME: orgName,
      ORDER_NUMBER: sampleOrder.orderNumber,
      ORDER_PICKUP_TIME: orderedAt,
      ORDER_TOTAL: sampleOrder.total,
      ORDERED_AT: orderedAt,
      TABLE_NUMBER: '',
      VEHICLE_DESCRIPTION: '',
    };
  },

  appendQrCode($parent, value, label) {
    const $code = $('<div class="receipt-live-qr"></div>');
    $parent.append($code);
    QRCode.toDataURL(value, { width: 128, margin: 0 })
      .then((url) => {
        if (this.el.contains($code[0])) {
          $code.prepend($('<img>').attr({ src: url, alt: label }));
        }
      })
      .catch(() => {
        $code.prepend($('<span></span>').text('QR code unavailable'));
      });
  },

  appendLoyaltyCode($paper) {
    const loyaltySchema = app.location?.getLoyaltyI9nSchema();
    const loyaltyIntegrationId = Object.entries(app.location?.get('integrationById') || {}).find(
      ([, integration]) => integration.system === loyaltySchema?.system,
    )?.[0];
    const loyalty = loyaltyIntegrationId && app.integrationList?.get(loyaltyIntegrationId);
    if (
      !loyalty ||
      ![IntegrationSystem.Punchh, IntegrationSystem.PunchhOlo].includes(loyalty.get('system')) ||
      !loyalty.getSyncedProp('printBarcodes') ||
      loyalty.get('printerBarcodeType') === PunchhPrinterBarcodeType.None
    ) {
      return;
    }
    const header = loyalty.getSyncedProp('header');
    if (header) {
      $paper.append($('<div class="receipt-live-loyalty"></div>').text(header));
    }
    if (
      !loyalty.get('printerBarcodeType') ||
      loyalty.get('printerBarcodeType') === PunchhPrinterBarcodeType.QR
    ) {
      this.appendQrCode($paper, 'SAMPLE-PUNCHH-KEY', 'Sample loyalty QR code');
    } else {
      $paper.append($('<div class="receipt-live-code"></div>').text('Sample loyalty barcode'));
    }
    for (let index = 1; index <= 5; index += 1) {
      const trailer = loyalty.getSyncedProp(`trailer${index}`);
      if (trailer) {
        $paper.append($('<div class="receipt-live-loyalty"></div>').text(trailer));
      }
    }
  },

  bindImages() {
    this.stopListening();
    ['receiptHeaderImage', 'receiptFooterImage'].forEach((field) => {
      const imageViews = this.fieldGroupView.fieldViewsByField[field]?.imageViews || [];
      imageViews.forEach((imageView) => {
        if (imageView.imagePreview) {
          this.listenTo(imageView.imagePreview, 'receipt-image-rendered', this.render);
        }
      });
    });
  },

  appendTemplate($paper, value, tokens) {
    if (!value) {
      return;
    }
    const $block = $('<div class="receipt-live-template"></div>');
    const template = String(value).replace(/{{\s*([A-Z_]+)\s*}}/g, (match, key) => {
      return escapeTemplateValue(tokens[key] ?? '');
    });
    const document = new DOMParser().parseFromString(template, 'text/html');
    const appendNode = (node, $parent) => {
      if (node.nodeType === Node.TEXT_NODE) {
        $parent.append(document.createTextNode(node.textContent));
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }
      const tag = node.nodeName.toLowerCase();
      if (tag === 'br') {
        return;
      }
      if (tag === 'qrcode') {
        const content = node.textContent?.trim();
        if (content) {
          this.appendQrCode($parent, content, 'Template QR code');
        }
        return;
      }
      if (!['b', 's1', 's2', 's3'].includes(tag)) {
        return;
      }
      const $target = $('<span></span>').addClass(`receipt-live-${tag}`);
      Array.from(node.childNodes).forEach((child) => appendNode(child, $target));
      $parent.append($target);
    };
    Array.from(document.body.childNodes).forEach((node) => appendNode(node, $block));
    $paper.append($block);
  },

  appendImage($paper, field) {
    const imageViews = this.fieldGroupView.fieldViewsByField[field]?.imageViews || [];
    const preview = imageViews.find((imageView) => imageView.getImage())?.imagePreview;
    if (!preview) {
      return;
    }
    const source = preview.receiptPreviewElement;
    if (!source) {
      return;
    }
    const $image = $('<div class="receipt-live-image"></div>');
    if (source instanceof HTMLCanvasElement) {
      const canvas = document.createElement('canvas');
      canvas.width = source.width;
      canvas.height = source.height;
      canvas.getContext('2d')?.drawImage(source, 0, 0);
      $image.append(canvas);
    } else {
      $image.append($(source).clone());
    }
    $paper.append($image);
  },

  appendRow($paper, label, amount, className = '') {
    $paper.append(
      $('<div class="receipt-live-row"></div>')
        .addClass(className)
        .append($('<span></span>').text(label), $('<span></span>').text(amount)),
    );
  },

  render() {
    const $paper = $('<div class="receipt-live-paper"></div>');
    const tokens = this.templateTokens();
    const headerText = this.value('receiptHeaderText');
    if (this.value('receiptHeaderImagePosition') === 'above') {
      this.appendImage($paper, 'receiptHeaderImage');
    }
    if (headerText) {
      this.appendTemplate($paper, headerText, tokens);
    } else {
      $paper.append(
        $('<div class="receipt-live-brand"></div>').text(tokens.BRAND_NAME),
        $('<div class="receipt-live-location"></div>').text(tokens.LOCATION_NAME),
      );
    }
    if (this.value('receiptHeaderImagePosition') !== 'above') {
      this.appendImage($paper, 'receiptHeaderImage');
    }
    $paper.append(
      $('<div class="receipt-live-order"></div>').text(tokens.ORDERED_AT),
      $('<div class="receipt-live-order"></div>').text(`To Go  ·  Order ${tokens.ORDER_NUMBER}`),
    );
    $paper.append(
      $('<div class="receipt-live-vendor"></div>')
        .addClass(`receipt-live-vendor-${this.value('vendorInstructionsSize') || 'large'}`)
        .text('Pickup at counter'),
    );
    const $body = $('<div class="receipt-live-body"></div>').toggleClass(
      'receipt-live-medium',
      this.value('guestReceiptBodyTextSize') === 'medium',
    );
    this.appendRow(
      $body,
      `1 × Sample item${this.value('printOrderedItemPluWithOrderedItems') ? '  [12345]' : ''}`,
      '$4.00',
    );
    this.appendRow(
      $body,
      `  + ${this.value('printModCodes') ? 'TOP  ' : ''}Extra topping`,
      '$1.00',
    );
    if (this.value('printDefaultMods') && this.value('printZeroDollarMods')) {
      this.appendRow($body, '  + Included topping', '$0.00');
    }
    if (this.value('printZeroDollarMods')) {
      this.appendRow($body, '  + Free topping', '$0.00');
    }
    if (this.value('printDeselectedMods')) {
      this.appendRow($body, '  − No onions', '');
    }
    this.appendRow($body, 'Subtotal', '$5.00', 'receipt-live-rule');
    this.appendRow($body, this.value('hideCouponCodes') ? 'Coupon' : 'WELCOME10 coupon', '−$1.00');
    if (this.value('includeTaxTotalOnReceipts')) {
      this.appendRow($body, 'Tax', '$0.40');
    }
    this.appendRow($body, 'Total', sampleOrder.total, 'receipt-live-total');
    $paper.append($body);
    if (this.value('printOrderNumberBarcode')) {
      $paper.append(
        $('<div class="receipt-live-code"></div>').text(
          this.value('printQRCodeWithPosOrderID') ? 'POS order QR code' : 'Order barcode',
        ),
      );
    }
    if (this.value('orderedItemPlusQrCodeConfig')) {
      this.appendQrCode($paper, 'SAMPLE-ITEM-PLU', 'Sample item PLU QR code');
    }
    this.appendLoyaltyCode($paper);
    if (this.value('printTransactionReceiptText')) {
      $paper.append(
        $('<div class="receipt-live-payment"></div>').text('Card payment  ·  Approved'),
      );
    }
    this.appendTemplate($paper, this.value('receiptFooterText'), tokens);
    this.appendImage($paper, 'receiptFooterImage');
    $paper.append($('<div class="receipt-live-powered"></div>').text('Kiosks Powered by Bite'));
    this.$el
      .empty()
      .append(
        $('<h3></h3>').text('Printed kiosk receipt preview'),
        $('<p></p>').text(
          'Location details come from this location. Guest, item, payment, and loyalty code data are samples. Layout varies by printer and order.',
        ),
        $paper,
      );
    return this;
  },
});
